/**
 * AI CHAT EVENT SCHEMA
 *
 * Canonical event types for the AI chat system. All provider output
 * (Claude Code, Codex, Gemini) is normalized to this schema. This is the contract
 * between the Rust backend and the frontend store.
 *
 * Replay guarantee: given the same AiEvent[] array, the frontend store
 * produces identical messages[], toolActivity[], and fileChanges[].
 */

// ── Provider & Session ──────────────────────────────────────────────

export type AiProvider = "claude-code" | "codex" | "gemini";

export type SessionState =
  | "idle"
  | "running"
  | "reviewing"
  | "completed"
  | "error";

// ── Event Kinds ─────────────────────────────────────────────────────

export type AiEventKind =
  | "text_delta"       // streaming text token
  | "text_complete"    // full message boundary
  | "tool_start"       // tool invoked
  | "tool_output"      // tool result
  | "file_change"      // file created/modified/deleted
  | "status"           // thinking, idle, tool_use
  | "cost"             // tokens + USD after message
  | "error"            // error
  | "session_start"    // session started (contains provider session ID)
  | "session_end"      // session ended (exit code)
  | "thinking"             // extended thinking delta
  | "thinking_complete"    // thinking block boundary
  | "permission_request"   // tool requesting permission
  | "permission_response"; // user approved/denied

// ── Event Payloads ──────────────────────────────────────────────────

export interface TextDeltaPayload {
  text: string;
}

export interface TextCompletePayload {
  message_id: string;
}

export interface ToolStartPayload {
  tool_id: string;
  tool_name: string;
  tool_input_summary: string;
}

export interface ToolOutputPayload {
  tool_id: string;
  status: "success" | "error";
  output_summary?: string;
}

export interface FileChangePayload {
  path: string;
  change_type: "created" | "modified" | "deleted";
}

export interface StatusPayload {
  status: "thinking" | "tool_use" | "idle";
}

export interface CostPayload {
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  duration_ms: number;
}

export interface ErrorPayload {
  message: string;
  recoverable: boolean;
}

export interface SessionStartPayload {
  provider_session_id?: string;
  cwd: string;
}

export interface SessionEndPayload {
  exit_code: number;
  provider_session_id?: string;
}

export interface ThinkingPayload {
  text: string;
}

export interface PermissionRequestPayload {
  tool_id: string;
  tool_name: string;
  tool_input_summary: string;
}

export interface PermissionResponsePayload {
  tool_id: string;
  approved: boolean;
}

// ── Payload Union ───────────────────────────────────────────────────

export type AiEventPayload =
  | TextDeltaPayload
  | TextCompletePayload
  | ToolStartPayload
  | ToolOutputPayload
  | FileChangePayload
  | StatusPayload
  | CostPayload
  | ErrorPayload
  | SessionStartPayload
  | SessionEndPayload
  | ThinkingPayload
  | PermissionRequestPayload
  | PermissionResponsePayload;

// ── Main Event Type ─────────────────────────────────────────────────

export interface AiEvent {
  session_id: string;
  seq: number;
  ts: string;
  source_provider: AiProvider;
  kind: AiEventKind;
  payload: AiEventPayload;
}

// ── Derived State Types ─────────────────────────────────────────────

export interface DerivedMessage {
  id: string;
  role: "user" | "assistant" | "system";
  provider?: AiProvider;
  content: string;
  isStreaming: boolean;
  costUsd?: number;
  eventSeqRange: [number, number];
}

export interface DerivedTool {
  id: string;
  toolName: string;
  inputSummary: string;
  status: "running" | "success" | "error";
  outputSummary?: string;
  eventSeq: number;
}

// ── Session Type ────────────────────────────────────────────────────

export interface AiChatSession {
  id: string;
  provider: AiProvider;
  state: SessionState;
  cwd: string;
  title: string;

  events: AiEvent[];
  messages: DerivedMessage[];
  toolActivity: DerivedTool[];
  fileChanges: FileChangePayload[];

  totalCostUsd: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  lastSeq: number;

  /** ISO timestamp of when the current turn started (set per user turn). */
  runStartedAt?: string;

  providerSessionId?: string;
}

// ── Provider Metadata ──────────────────────────────────────────────

export const PROVIDER_META: Record<AiProvider, { label: string; logo: string }> = {
  "claude-code": { label: "Claude", logo: "/claude-logo-png_seeklogo-554534.png" },
  codex: { label: "Codex", logo: "/openai-chatgpt-logo-icon-free-png.webp" },
  gemini: { label: "Gemini", logo: "/Google_Gemini_icon_2025.svg.png" },
};

// ── Guardrails ──────────────────────────────────────────────────────

export interface SessionGuardrails {
  max_cost_usd: number;
  max_duration_secs: number;
  max_events: number;
}

export const DEFAULT_GUARDRAILS: SessionGuardrails = {
  max_cost_usd: 5.0,
  max_duration_secs: 600,
  max_events: 10_000,
};

// ── Review Types ────────────────────────────────────────────────────

export interface ReviewArtifact {
  cwd: string;
  original_prompt: string;
  provider_summary: string;
  file_changes: FileChangePayload[];
  git_diff: string;
  test_results?: string;
  errors?: string[];
  total_cost_usd: number;
  event_count: number;
  duration_ms: number;
}

export interface ReviewFinding {
  severity: "critical" | "warning" | "suggestion";
  file: string;
  description: string;
}

export interface ReviewResult {
  verdict: "approved" | "request_changes";
  summary: string;
  findings: ReviewFinding[];
  suggested_actions: string[];
}

export type ReviewMode = "advisory" | "gated";

// ── Provider Status ─────────────────────────────────────────────────

export interface ProviderStatus {
  provider: AiProvider;
  available: boolean;
  path?: string;
  error?: string;
  install_hint?: string;
}

// ── Tauri Command Types ─────────────────────────────────────────────

export interface SessionInfo {
  session_id: string;
  provider: AiProvider;
  state: string;
  cwd: string;
  created_at: string;
}

export interface CreateSessionParams {
  provider: AiProvider;
  cwd: string;
  prompt: string;
  resume_session_id?: string;
  guardrails?: SessionGuardrails;
}
